import { ErrorBoundary } from "@/components/ErrorBoundary";
import { CalendarHeader } from "@/components/CalendarHeader";
import { CalendarGrid } from "@/components/CalendarGrid";
import { CalendarCompactLegend } from "@/components/CalendarCompactLegend";
import { getDaysArray, formatMonth } from "@/utils/calendar";
import { useCalendarState } from "@/hooks/use-calendar-state";
import { Suspense, useMemo, useCallback, memo } from "react";
import { useSubscription } from "@/hooks/use-subscription";
import { CalendarSubscriptionAlert } from "@/components/CalendarSubscriptionAlert";
import { isWithinInterval } from "date-fns";
import { cn } from "@/lib/utils";

// Type definitions
export type Volatility = 'low' | 'medium' | 'high';

interface EventDetails {
  title: string;
  event?: string;
  impact?: string;
  country?: string;
  description?: string;
  time?: string; 
  actual?: number;
  forecast?: number;
  previous?: number;
  unit?: string;
  score?: number;
}

export interface CalendarEvent {
  date: string;
  events: EventDetails[];
}

interface DayData {
  events?: EventDetails[];
  display_score?: number;
  threshold?: number;
  total_score?: number;
  daily_sum?: number;
  volatility?: string;
}

export interface CalendarProps {
  onDateClick?: (date: number) => void;
  onEventsChange?: (events: CalendarEvent[]) => void;
}

/**
 * Helper Functions
 */

/**
 * Determines if a date has high impact events
 */
const hasHighImpactEvents = (events?: EventDetails[]): boolean => {
  if (!events || events.length === 0) return false;
  return events.some(event => event.impact === 'high');
};

/**
 * Gets the earliest high impact event time
 */
const getFirstHighImpactEventTime = (events?: EventDetails[]): string | null => {
  if (!events || events.length === 0) return null;
  
  const highImpactEvents = events.filter(event => event.impact === 'high');
  if (highImpactEvents.length === 0) return null;
  
  // Sort events by time, handling missing time values
  const sortedEvents = [...highImpactEvents].sort((a, b) => {
    if (!a.time) return 1;
    if (!b.time) return -1;
    return a.time.localeCompare(b.time);
  });
  
  return sortedEvents[0]?.time || null;
};

/**
 * Main Calendar component
 * Renders a monthly calendar view with economic events
 */
export const Calendar = memo(({ 
  onDateClick, 
  onEventsChange 
}: CalendarProps) => {
  const {
    session,
    isSessionLoading,
    currentDate,
    handlePrevMonth,
    handleNextMonth,
    selectedCountries,
    handleCountrySelect,
    handleClearFilters,
    selectedDate,
    handleDateClick,
    data,
    isDataLoading,
    error,
    isRefetching,
    isMounted
  } = useCalendarState();

  // Get subscription status for free tier limitations
  const {
    isSubscribed,
    isTrialing,
    calendarDaysLimit
  } = useSubscription();
  
  // Calculate allowed date range for free users
  const allowedDateRange = (!isSubscribed && !isTrialing && calendarDaysLimit !== Infinity) ? {
    startDate: new Date(Date.now() - calendarDaysLimit * 24 * 60 * 60 * 1000),
    endDate: new Date(Date.now() + calendarDaysLimit * 24 * 60 * 60 * 1000)
  } : null;

  // Check if query limits should be disabled (development mode)
  const disableQueryLimits = import.meta.env.VITE_DISABLE_QUERY_LIMITS === 'true';

  // Check if user has access limitations
  // In development mode with disabled query limits, ignore access limitations
  const hasAccessLimitations = !disableQueryLimits && !isSubscribed && !isTrialing && allowedDateRange;
  
  // Log whether query limits are being applied
  console.log('Calendar: Query limits disabled:', disableQueryLimits, 'Has access limitations:', hasAccessLimitations);

  /**
   * Transforms server data into the calendar days array
   */
  const days = useMemo(() => {
    return getDaysArray(currentDate).map(day => {
      // Skip empty days (padding in calendar grid)
      if (day.date === 0) return day;
      
      // Create date object for the current day
      const date = new Date(Date.UTC(
        currentDate.getFullYear(),
        currentDate.getMonth(),
        day.date
      ));
      
      // Get date as string for lookup in data
      const dateStr = date.toISOString().split('T')[0];
      const dayData = data?.month?.[dateStr] as DayData | undefined;
      
      // Check if date is disabled for free users
      let disabled = false;
      if (hasAccessLimitations) {
        // When query limits are applied, check if date is within allowed range
        disabled = !isWithinInterval(date, {
          start: allowedDateRange.startDate,
          end: allowedDateRange.endDate
        });
      } else if (disableQueryLimits) {
        // In development mode with query limits disabled, no dates are disabled
        disabled = false;
      }
      
      // Map volatility value to type, defaulting to low
      let volatility: Volatility = 'low';
      if (dayData?.volatility === 'medium') volatility = 'medium';
      if (dayData?.volatility === 'high') volatility = 'high';
      
      // Extract event information
      const hasHighImpactEvts = hasHighImpactEvents(dayData?.events);
      const majorEventTime = getFirstHighImpactEventTime(dayData?.events);
      
      // Return day with calculated properties
      return {
        ...day,
        events: {
          count: dayData?.events?.length || 0,
          value: dayData?.display_score || 0,
          threshold: dayData?.threshold,
          totalScore: dayData?.total_score,
          dailySum: dayData?.daily_sum,
          volatility,
          majorEventTime,
          hasHighImpactEvents: hasHighImpactEvts
        },
        disabled
      };
    });
  }, [currentDate, data, allowedDateRange, hasAccessLimitations, disableQueryLimits]);

  /**
   * Transform data for the events panel
   */
  const eventsForPanel = useMemo(() => {
    if (!data?.month) return [];
    
    return Object.entries(data.month)
      .map(([date, dayData]) => ({
        date,
        events: (dayData.events || []).map(event => {
          // Handle the case where event name might be in 'event' property instead of 'title'
          const eventData = event as EventDetails & { event?: string };
          return {
            ...event,
            title: eventData.title || eventData.event || "Untitled Event"
          } as EventDetails;
        })
      }))
      .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
  }, [data]);

  /**
   * Date click handler with access check for free users
   */
  const handleDateClickMemo = useCallback((date: number) => {
    // Check access for free users
    if (hasAccessLimitations) {
      const clickedDate = new Date(Date.UTC(
        currentDate.getFullYear(),
        currentDate.getMonth(),
        date
      ));
      
      // Skip if outside allowed date range
      const isAllowed = isWithinInterval(clickedDate, {
        start: allowedDateRange.startDate,
        end: allowedDateRange.endDate
      });
      
      if (!isAllowed) return;
    }
    
    // Call internal handler first
    handleDateClick(date);
    
    // Call external handler if provided
    if (onDateClick) {
      onDateClick(date);
    }
  }, [handleDateClick, onDateClick, currentDate, hasAccessLimitations, allowedDateRange]);

  // Notify parent component of events changes
  useMemo(() => {
    if (onEventsChange && eventsForPanel.length > 0) {
      onEventsChange(eventsForPanel);
    }
  }, [eventsForPanel, onEventsChange]);

  // Handle loading state
  if (isSessionLoading) {
    return (
      <div className="p-4 text-center text-muted-foreground">
        Loading calendar...
      </div>
    );
  }

  // Handle unauthorized state
  if (!session?.user?.id && !disableQueryLimits) {
    return (
      <div className="p-4 text-center text-muted-foreground">
        Please sign in to view the calendar.
      </div>
    );
  }
  
  // In development mode with disabled query limits, we can proceed without a session

  return (
    <ErrorBoundary>
      <Suspense fallback={<div className="p-4 text-center text-muted-foreground">Loading...</div>}>
        <div className={cn(
          "space-y-3 sm:space-y-4 md:space-y-6 lg:space-y-8",
          "animate-in fade-in-50 duration-500"
        )}>
          {/* Subscription alert for free users */}
          {hasAccessLimitations && (
            <CalendarSubscriptionAlert />
          )}
          
          {/* Main calendar container with enhanced world-class styling */}
          <div className={cn(
            "relative overflow-hidden",
            "rounded-lg sm:rounded-xl md:rounded-2xl lg:rounded-3xl",
            "border border-slate-600/40",
            "bg-gradient-to-br from-slate-900/80 via-slate-800/60 to-slate-900/80",
            "shadow-xl sm:shadow-2xl shadow-black/50",
            "backdrop-blur-lg"
          )}>
            {/* Enhanced ambient lighting effects */}
            <div className="absolute inset-0 bg-gradient-to-br from-blue-500/8 via-purple-500/5 to-cyan-500/6 pointer-events-none" />
            
            {/* Premium glass morphism overlay */}
            <div className="absolute inset-0 bg-gradient-to-br from-white/[0.02] via-transparent to-white/[0.01] pointer-events-none" />
            
            {/* Enhanced calendar header */}
            <CalendarHeader
              currentMonth={formatMonth(currentDate)}
              onPrevMonth={handlePrevMonth}
              onNextMonth={handleNextMonth}
              selectedCountries={selectedCountries}
              onCountrySelect={handleCountrySelect}
              onClearFilters={handleClearFilters}
            />
            
            {/* Premium compact legend */}
            <CalendarCompactLegend />
            
            {/* World-class calendar grid with enhanced styling */}
            <div className="relative">
              {/* Subtle grid pattern background */}
              <div className="absolute inset-0 opacity-[0.02] pointer-events-none">
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent" 
                     style={{ backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 1px, rgba(255,255,255,0.05) 1px, rgba(255,255,255,0.05) 2px)' }} />
              </div>
              
              <CalendarGrid 
                days={days} 
                onDateClick={handleDateClickMemo}
                currentMonth={currentDate}
                isLoading={isRefetching}
              />
            </div>
            
            {/* Premium inner border glow */}
            <div className="absolute inset-0 rounded-lg sm:rounded-xl md:rounded-2xl lg:rounded-3xl pointer-events-none border border-white/[0.05] shadow-inner shadow-white/[0.02]" />
          </div>
        </div>
      </Suspense>
    </ErrorBoundary>
  );
});

Calendar.displayName = 'Calendar';